import { useCallback, useEffect, useMemo } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { chains as assets } from "@sablier/v2-assets";
import { DEFAULT_CHAIN_ID } from "@sablier/v2-constants";
import { useResolvedTypedAddress } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { guards } from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { vendors } from "@sablier/v2-utils";
import { useRouter } from "next/router";
import { contracts, supported, tabs } from "~/client/constants";
import {
  useModalSearch,
  useResetter,
  useSearchAccessor,
  useSearchField,
  useSearchForm,
  useStreamStoreAccessor,
} from "~/client/hooks";
import type { IFilter } from "@sablier/v2-types";
import type { IStreamOption } from "~/client/contexts/Form/Search";

export function useInit() {
  const access = useSearchAccessor();
  const { data } = useModalSearch();

  useEffect(() => {
    const state = access();
    const update = state.api.updateFields;

    const filter: IFilter = _.toFilter(data.filter || {});

    const chain =
      supported.find((i) => i.id === data.filter?.chainId)?.local ||
      supported.find((i) => i.id === DEFAULT_CHAIN_ID)?.local;

    const aliases = Stream.findAliases(chain?.chainId, contracts);

    const ids = filter.streamIds
      ?.map((id) => {
        const identity = Stream.doIdentify(id, aliases);

        return {
          title: _.toShort(identity.withAddress, 8, -8),
          subtitle: identity.withAlias?.toUpperCase(),
          withAddress: identity.withAddress,
          withAlias: identity.withAlias,
        };
      })
      .filter((item) => !_.isNilOrEmptyString(item.withAddress));

    update({
      recipient: {
        value: filter.recipient,
      },
      sender: {
        value: filter.sender,
      },
      chain: {
        value: chain,
      },
      streams: {
        value: ids || [],
      },
    });
  }, [access, data]);
}

export function useWatcher() {}

export function useFieldRecipient() {
  const access = useSearchAccessor();
  const chain = useSearchField("chain");
  const recipient = useSearchField("recipient");
  const { t } = useT();

  const label = useMemo(
    () => ({
      value: _.capitalize(t("words.recipient")),
    }),
    [t],
  );

  /** --- When the input changes values, update the resolution --- */
  const resolution = useResolvedTypedAddress(
    recipient.field.value,
    chain.field.value?.chainId,
  );

  /** --- When the field loses focus, test vales without resolution (we're working async) --- */
  const onBlur = useCallback(() => {
    const state = access();
    const field = state.fields.recipient;
    const chainId = state.fields.chain.value?.chainId;
    const update = state.api.updateFields;

    if (_.isNilOrEmptyString(field.value)) {
      update({
        recipient: {
          isFocused: false,
          warning: undefined,
        },
      });
    } else {
      update({
        recipient: {
          isFocused: false,
          warning: guards.validateAccount({
            t,
            chainId,
            value: field.value,
          }),
        },
      });
    }
  }, [access, t]);

  const onFocus = useCallback(() => {
    access().api.updateFields({ recipient: { isFocused: true } });
  }, [access]);

  /** --- When the input changes values, reset dependencies --- */
  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      update({
        recipient: { value, resolution: undefined, warning: undefined },
      });
    },
    [access],
  );

  /** --- When the chain is changed, re-test the values --- */
  useEffect(() => {
    const state = access();
    const field = state.fields.recipient;
    if (!_.isNilOrEmptyString(field.value)) {
      onBlur();
    }
  }, [access, chain, onBlur]);

  /** --- When resolution happens, move its values in the store --- */
  useEffect(() => {
    const state = access();
    const update = state.api.updateFields;
    update({
      recipient: {
        isLoading: resolution.isLoading,
        resolution: _.pick(resolution, ["address", "ens", "preview", "short"]),
      },
    });
  }, [access, resolution]);

  /** --- When the field moves out-of-focus and the resolution is ready, validate it --- */
  useEffect(() => {
    if (
      !recipient.field.isFocused &&
      !recipient.field.isLoading &&
      !_.isNil(recipient.field.resolution)
    ) {
      const state = access();
      const chainId = state.fields.chain.value?.chainId;
      const update = state.api.updateFields;

      if (!_.isNilOrEmptyString(recipient.field.value)) {
        update({
          recipient: {
            warning: guards.validateAccount({
              t,
              address: recipient.field.resolution.address,
              chainId,
              isResolved: true,
              value: recipient.field.value,
            }),
          },
        });
      }
    }
  }, [recipient, access, t]);

  return {
    isLoading: resolution.isLoading,
    label,
    onBlur,
    onChange,
    onFocus,
    recipient,
  };
}

export function useFieldSender() {
  const access = useSearchAccessor();
  const chain = useSearchField("chain");
  const sender = useSearchField("sender");
  const { t } = useT();

  /** --- When the input changes values, update the resolution --- */
  const resolution = useResolvedTypedAddress(
    sender.field.value,
    chain.field.value?.chainId,
  );

  const label = useMemo(
    () => ({
      value: _.capitalize(t("words.sender")),
      icon: InformationCircleIcon,
      isIconLast: true,
      tooltip: {
        value: t("tooltips.sender"),
      },
    }),
    [t],
  );

  /** --- When the field loses focus, test vales without resolution (we're working async) --- */
  const onBlur = useCallback(() => {
    const state = access();
    const field = state.fields.sender;
    const update = state.api.updateFields;
    const chainId = state.fields.chain.value?.chainId;

    if (_.isNilOrEmptyString(field.value)) {
      update({
        sender: {
          isFocused: false,
          warning: undefined,
        },
      });
    } else {
      update({
        sender: {
          isFocused: false,
          warning: guards.validateAccount({
            t,
            chainId,
            value: field.value,
          }),
        },
      });
    }
  }, [access, t]);

  const onFocus = useCallback(() => {
    access().api.updateFields({ sender: { isFocused: true } });
  }, [access]);

  /** --- When the input changes values, reset dependencies --- */
  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      update({ sender: { value, resolution: undefined, warning: undefined } });
    },
    [access],
  );

  /** --- When the chain is changed, re-test the values --- */
  useEffect(() => {
    const state = access();
    const field = state.fields.sender;
    if (!_.isNilOrEmptyString(field.value)) {
      onBlur();
    }
  }, [access, chain, onBlur]);

  /** --- When resolution happens, move its values in the store --- */
  useEffect(() => {
    const state = access();
    const update = state.api.updateFields;
    update({
      sender: {
        isLoading: false,
        resolution: _.pick(resolution, ["address", "ens", "preview", "short"]),
      },
    });
  }, [access, resolution]);

  /** --- When the field moves out-of-focus and the resolution is ready, validate it --- */
  useEffect(() => {
    if (
      !sender.field.isFocused &&
      !sender.field.isLoading &&
      !_.isNil(sender.field.resolution)
    ) {
      const state = access();
      const update = state.api.updateFields;
      const chainId = state.fields.chain.value?.chainId;

      if (!_.isNilOrEmptyString(sender.field.value)) {
        update({
          sender: {
            warning: guards.validateAccount({
              t,
              chainId,
              address: sender.field.resolution.address,
              isResolved: true,
              value: sender.field.value,
            }),
          },
        });
      }
    }
  }, [sender, access, t]);

  return {
    sender,
    isLoading: resolution.isLoading,
    label,
    onBlur,
    onChange,
    onFocus,
  };
}

export function useFieldChain() {
  const access = useSearchAccessor();
  const chain = useSearchField("chain");
  const value = useMemo(
    () =>
      !_.isNil(chain.field.value)
        ? {
            id: _.toString(chain.field.value.chainId),
            image: chain.field.value.image,
            title: _.toString(chain.field.value.name),
          }
        : undefined,
    [chain],
  );

  const source = useMemo(
    () =>
      supported.map((item) => ({
        id: _.toString(item.local.chainId),
        image: assets[item.id],
        title: _.toString(item.local.name),
      })),
    [],
  );

  const onChange = useCallback(
    (value: { id: string; title: string }) => {
      const state = access();
      const update = state.api.updateFields;
      const item = supported.find((item) => item.id === _.toNumber(value.id));
      if (!_.isNil(item)) {
        update({
          chain: {
            value: { ...item.local, image: assets[item.id] },
            warning: undefined,
          },
        });
      }
    },
    [access],
  );

  return {
    chain,
    source,
    onChange,
    value,
  };
}

export function useFieldStreams() {
  const access = useSearchAccessor();
  const streams = useSearchField("streams");
  const chain = useSearchField("chain");
  const { t } = useT();

  const chainId = useMemo(() => chain.field.value?.chainId, [chain]);
  const aliases = useMemo(
    () => Stream.findAliases(chainId, contracts),
    [chainId],
  );

  const onBlur = useCallback(() => {
    const state = access();
    const chainId = state.fields.chain.value?.chainId;
    const current = state.fields.streams.current;
    const update = state.api.updateFields;

    const streams = state.fields.streams.value.map((stream) => ({
      ...stream,
      warning: guards.validateStreamIdentifier({
        t,
        chainId,
        supported: aliases,
        value: stream.withAlias,
      }),
    }));

    const mismatch = streams.find(
      (stream) => !_.isNilOrEmptyString(stream.warning),
    )?.warning;

    if (_.isNilOrEmptyString(current)) {
      update({
        streams: {
          value: streams,
          warning: mismatch,
        },
      });
    } else {
      update({
        streams: {
          value: streams,
          warning:
            mismatch ||
            guards.validateStreamIdentifier({
              t,
              chainId,
              value: current,
              supported: aliases,
            }),
        },
      });
    }
  }, [access, aliases, t]);

  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      update({ streams: { current: value, warning: undefined } });
    },
    [access],
  );

  const onAdd = useCallback(() => {
    const state = access();
    const streams = state.fields.streams;
    const update = state.api.updateFields;

    if (
      !_.isNilOrEmptyString(streams.warning) ||
      _.isNilOrEmptyString(streams.current)
    ) {
      return;
    }

    const identity = Stream.doIdentify(streams.current, aliases);

    if (
      !_.isNilOrEmptyString(identity.withAddress) &&
      !streams.value.find((item) => item.withAddress === identity.withAddress)
    ) {
      update({
        streams: {
          current: undefined,
          value: [
            ...streams.value,
            {
              title: _.toShort(identity.withAddress, 8, -8),
              subtitle: identity.withAlias,
              withAddress: identity.withAddress,
              withAlias: identity.withAlias,
            },
          ],
        },
      });
    } else {
      update({
        streams: {
          current: undefined,
        },
      });
    }
  }, [access, aliases]);

  const onRemove = useCallback(
    (value: IStreamOption) => {
      const state = access();
      const streams = state.fields.streams;
      const update = state.api.updateFields;

      update({
        streams: {
          value: streams.value.filter(
            (item) => item.withAddress !== value.withAddress,
          ),
        },
      });
    },
    [access],
  );

  useEffect(() => {
    onBlur();
  }, [access, aliases, chainId, onBlur]);

  return {
    onAdd,
    onBlur,
    onChange,
    onRemove,
    streams,
  };
}

export function useFormPrepare() {
  const access = useStreamStoreAccessor();
  const { search: reset } = useResetter();
  const router = useRouter();
  const { doClose } = useModalSearch();
  const fields = useSearchForm((state) => state.fields);

  const isReady = useMemo(
    () =>
      _.isNil(
        Object.values(fields).find(
          (field) => !_.isNilOrEmptyString(field.warning),
        ),
      ),
    [fields],
  );

  const onSuggest = useCallback(() => {
    if (isReady) {
      const filter: IFilter = _.toFilter({
        chainId: DEFAULT_CHAIN_ID,
      });

      if (!_.isEqual(filter, access().search?.filter)) {
        void reset();
      }

      const direction = tabs.dashboard.search.builder({
        c: _.toString(filter.chainId),
        p: filter.sender,
        i: filter.streamIds,
      });

      void router.push(direction, undefined, { shallow: true });
      doClose();
    }
  }, [access, doClose, isReady, router, reset]);

  const onConfirm = useCallback(() => {
    if (isReady) {
      const recipient = fields.recipient.resolution?.address;
      const sender = fields.sender.resolution?.address;
      const streamIds = fields.streams.value.map((item) => item.withAlias);

      const filter: IFilter = _.toFilter({
        chainId: fields.chain.value?.chainId || DEFAULT_CHAIN_ID,
        sender,
        recipient,
        streamIds,
      });

      if (!_.isEqual(filter, access().search?.filter)) {
        void reset();
      }

      const direction = tabs.dashboard.search.builder({
        c: _.toString(filter.chainId),
        s: filter.sender,
        r: filter.recipient,
        i: filter.streamIds,
      });

      vendors.track.log((events) => events.performSearch(direction));

      void router.push(direction, undefined, { shallow: true });
      doClose();
    }
  }, [access, doClose, fields, isReady, router, reset]);

  const onClear = useCallback(() => {
    doClose();
  }, [doClose]);

  return {
    isReady,
    onConfirm,
    onClear,
    onSuggest,
  };
}
